import React, { ReactNode, useState, useMemo, useEffect, useRef } from 'react'
import { Col, Row, Badge } from 'react-bootstrap'
import { useFetchUserProfileQuery } from '../../../services/authApi'
import { useLocation } from 'react-router-dom'
import { useAppSelector } from '../../../store/hooks'
import {
  Image,
  Tag,
  Box,
  Activity,
  Rocket,
  ChevronRight,
  BarChart2,
  MessageCircle,
  Database,
  Brain,
  Settings,
  Wrench,
  Download,
  BarChart3,
  Package,
  FolderKanban,
} from 'lucide-react'
import styled from 'styled-components'
import { toTitleCase } from '../../../utils/helpers'
import { selectSelectedProjectId } from '../../../store/slices/projectSlice'
import { useGetProjectQuery } from '../../../services/projectsApi'
import { useDatasetStats } from '../../../utils/datasetStats'
import { useGetTagsQuery } from '../../../services/tagApi'
import { useFetchModelsQuery } from '../../../services/modelsApi'
import { AIChatModal } from '../../modals/AIChatModal'
import { Model } from '../../../types/models/model'
import { PageHeader } from '../../common'

interface HeaderRowProps {
  project?: {
    name?: string
    annotation_type?: string
  }
  pageHeaderText: {
    title: string
    subtitle: string
  }
  controls?: ReactNode
}

// Removed complex styled component - will use Bootstrap classes

// Removed complex styled component - will use Bootstrap classes

// Removed complex styled component - will use Bootstrap classes

// Simplified styled components - keeping only essential ones
const StatsRow = styled(Row)`
  position: relative;
  z-index: 1000;
  margin-top: 1.2rem;
  padding: 0;
  margin-left: 0;
  margin-right: 0;
  width: 100%;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.7);
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 36px;
`

const HeaderContainer = styled.div`
  position: relative;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0;
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
`

const StatItem = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.5rem;
  margin: 0;
  border-right: 1px solid rgba(255, 255, 255, 0.15);
  transition: all 0.2s ease;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.05);
  height: 32px;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }

  &:last-child {
    border-right: none;
  }

  .icon {
    color: rgba(255, 255, 255, 0.5);
    width: 16px;
    height: 16px;
  }

  .stat-content {
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }

  .stat-value {
    font-size: 0.95rem;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.9);
    margin: 0;
    line-height: 1;
  }

  .stat-label {
    font-size: 0.8rem;
    color: rgba(255, 255, 255, 0.5);
    margin: 0;
    line-height: 1;
  }
`

// Removed complex styled components - will use Bootstrap classes and minimal custom styling
const StatsIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(40, 44, 48, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  width: 36px;
  height: 36px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);

  &:hover {
    background: rgba(50, 54, 58, 0.8);
    transform: translateX(-50%) scale(1.05);
  }

  svg {
    color: rgb(255, 81, 0);
    filter: drop-shadow(0 0 3px rgba(0, 229, 255, 0.5));
  }
`

const AIChatIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(85, 60, 154, 0.2);
  border: 1px solid rgba(85, 60, 154, 0.4);
  border-radius: 50%;
  width: 36px;
  height: 36px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);

  &:hover {
    background: rgba(85, 60, 154, 0.3);
    border-color: rgba(85, 60, 154, 0.6);
    transform: translateY(-50%) scale(1.05);
  }

  svg {
    color: #553c9a;
    filter: drop-shadow(0 0 3px rgba(85, 60, 154, 0.5));
  }
`

const BetaTag = styled.div`
  position: absolute;
  top: -8px;
  right: -12px;
  background: #553c9a;
  color: white;
  font-size: 8px;
  font-weight: bold;
  padding: 1px 4px;
  border-radius: 3px;
  letter-spacing: 0.5px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.2);
`

const HeaderRowContainer = styled.div`
  width: 100%;
  position: relative;
  z-index: 1001;
  background-color: transparent;
  display: flex;
  justify-content: center;
  padding: 0.75rem;
  align-items: center;
`

const ProjectTypeBadge = styled(Badge)<{ $isCollapsed?: boolean }>`
  padding: 0.375rem 0.75rem;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  border-radius: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(8px);
  text-transform: uppercase;
  margin-left: auto;

  ${(props) =>
    props.$variant === 'classification' &&
    `
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.9) 0%, rgba(37, 99, 235, 0.8) 100%);
    color: #ffffff;
    border-color: rgba(59, 130, 246, 0.6);
  `}

  ${(props) =>
    props.$variant === 'oriented_bounding_box' &&
    `
    background: linear-gradient(135deg, rgba(34, 211, 238, 0.9) 0%, rgba(20, 184, 166, 0.8) 100%);
    color: #ffffff;
    border-color: rgba(34, 211, 238, 0.6);
  `}

  ${(props) =>
    props.$variant === 'bounding_box' &&
    `
    background: linear-gradient(135deg, rgba(168, 85, 247, 0.9) 0%, rgba(147, 51, 234, 0.8) 100%);
    color: #ffffff;
    border-color: rgba(168, 85, 247, 0.6);
  `}
`

// Page header configuration based on route
const getPageHeaderConfig = (
  pathname: string,
  project?: { datasets?: { length: number }; name?: string },
  user?: { first_name?: string; last_name?: string }
) => {
  const route = pathname.split('/')[1]

  switch (route) {
    case 'datasets':
      return {
        title: 'Datasets',
        subtitle: `Explore and manage ${
          project?.datasets?.length || 0
        } available datasets in your project`,
        icon: <Database size={24} />,
      }
    case 'models':
      return {
        title: 'Models',
        subtitle: 'Manage and train your AI models',
        icon: <Brain size={24} />,
      }
    case 'training':
      return {
        title: 'Training',
        subtitle: 'Train and manage your AI models',
        icon: <Settings size={24} />,
      }
    case 'labeling':
      return {
        title: 'Labeling',
        subtitle: 'Annotate and manage your datasets',
        icon: <Tag size={24} />,
      }
    case 'tools':
      return {
        title: 'Tools',
        subtitle: 'Advanced AI tools and inference capabilities',
        icon: <Wrench size={24} />,
      }
    case 'deployment':
      return {
        title: 'Deployment',
        subtitle: 'Deploy your trained models',
        icon: <Rocket size={24} />,
      }
    case 'export':
      return {
        title: 'Export Data',
        subtitle:
          'Export your project data in various formats for external use, backup, or deployment',
        icon: <Download size={24} />,
      }
    case 'overview':
      return {
        title: 'Project Overview',
        subtitle: `Overview of ${project?.name || 'your project'}`,
        icon: <BarChart3 size={24} />,
      }
    case 'versions':
      return {
        title: 'Model Versions',
        subtitle: 'Manage different versions of your models',
        icon: <Package size={24} />,
      }
    case 'inference':
      return {
        title: 'Inference',
        subtitle: 'Run inference on your trained models',
        icon: <Activity size={24} />,
      }
    case 'projects':
      return {
        title: `Hi, ${user?.first_name || 'User'}`,
        subtitle: 'Manage your projects and get started',
        icon: <FolderKanban size={24} />,
      }
    default:
      return {
        title: `Welcome back, ${user?.first_name || 'User'}!`,
        subtitle: project?.name ? `Working on ${project.name}` : 'Select a project to get started',
        icon: <FolderKanban size={24} />,
      }
  }
}

export const HeaderRow: React.FC<HeaderRowProps> = ({ project, controls }) => {
  const { data: user } = useFetchUserProfileQuery()
  const location = useLocation()
  const currentSection = location.pathname.split('/')[1]
  const isProjectsPage = currentSection === 'projects'
  const isProfilePage = currentSection === 'profile'
  const shouldHideStatsAndChat = isProjectsPage || isProfilePage
  const [showAIChatModal, setShowAIChatModal] = useState(false)
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const { data: projectData } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })

  const { data: tagsData } = useGetTagsQuery(
    {
      project_id: projectData?.id || '',
      dataset_id: projectData?.datasetid || '',
    },
    {
      skip: !selectedProjectId || !projectData?.datasetid,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    }
  )
  const { data: modelsData } = useFetchModelsQuery(
    { project_id: selectedProjectId || '' },
    {
      skip: !selectedProjectId,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    }
  )
  const headerDatasetStats = useDatasetStats(
    selectedProjectId || '',
    projectData?.datasetid || '',
    []
  )
  const headerAggregate = headerDatasetStats.__aggregate__

  // Calculate project statistics
  const projectStats = useMemo(() => {
    return {
      totalImages: projectData?.count || 0,
      totalAnnotations: headerAggregate?.total_annotations || 0,
      totalClasses: tagsData?.tags?.length || 0,
      totalModels: modelsData?.models?.length || 0,
      deployedModels: modelsData?.models?.filter(
        (model: Model) => model.deployment.status.toLowerCase() === 'in_service'
      ),
    }
  }, [projectData, headerAggregate?.total_annotations, tagsData, modelsData])

  // Add state for showing stats
  const [showStats, setShowStats] = useState(true)
  const statsTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Add a state to track if stats were manually opened
  const [manuallyOpened, setManuallyOpened] = useState(false)

  // Auto-hide stats after 5/10 seconds
  useEffect(() => {
    if (statsTimeoutRef.current) {
      clearTimeout(statsTimeoutRef.current)
    }

    // Set new timeout when stats are shown
    if (showStats) {
      // Use 10 seconds if manually opened, 5 seconds for route changes
      const timeoutDuration = manuallyOpened ? 10000 : 5000

      statsTimeoutRef.current = setTimeout(() => {
        setShowStats(false)
        setManuallyOpened(false) // Reset the manual flag when closing
      }, timeoutDuration)
    }

    return () => {
      if (statsTimeoutRef.current) {
        clearTimeout(statsTimeoutRef.current)
      }
    }
  }, [showStats, manuallyOpened, location.pathname])

  // Set initial stats display on route change
  useEffect(() => {
    setShowStats(true)
    setManuallyOpened(false) // Route change is not manual

    return () => {
      if (statsTimeoutRef.current) {
        clearTimeout(statsTimeoutRef.current)
      }
    }
  }, [location.pathname])

  const pageHeaderConfig = getPageHeaderConfig(location.pathname, project, user)

  return (
    <HeaderRowContainer>
      <HeaderContainer>
        <Row className="w-100 g-3 px-2">
          {/* Page Header */}
          <Col md={12}>
            <PageHeader
              title={pageHeaderConfig.title}
              subtitle={pageHeaderConfig.subtitle}
              icon={pageHeaderConfig.icon}
              actions={
                projectData?.annotation_type && !isProjectsPage && !isProfilePage ? (
                  <ProjectTypeBadge $variant={projectData.annotation_type}>
                    {toTitleCase(projectData.annotation_type)}
                  </ProjectTypeBadge>
                ) : undefined
              }
            />
          </Col>
        </Row>

        {/* Stats Row with Route Header */}
        <StatsRow>
          <div className="d-flex align-items-center w-100 px-3 position-relative">
            {/* Breadcrumbs */}
            <div className="position-absolute start-0 top-50 translate-middle-y">
              <h3 className="text-white mb-0 d-flex align-items-center gap-3">
                {(() => {
                  const segments = location.pathname.split('/').filter(Boolean)
                  if (segments.length > 0) {
                    const mainSection = segments[0].charAt(0).toUpperCase() + segments[0].slice(1)
                    const lastSegment = segments[segments.length - 1]
                    let finalSegment = /^[0-9a-f]{24}$/i.test(lastSegment)
                      ? null
                      : lastSegment.charAt(0).toUpperCase() + lastSegment.slice(1)

                    if (lastSegment === 'adaptive-active-labeling') {
                      finalSegment = 'AAL'
                    }

                    return (
                      <>
                        <span className="fw-bold">{mainSection}</span>
                        {finalSegment && finalSegment !== mainSection && (
                          <>
                            <ChevronRight size={20} className="text-muted" />
                            <span className="text-primary">{finalSegment}</span>
                          </>
                        )}
                      </>
                    )
                  }
                  return ''
                })()}
              </h3>
            </div>

            {/* Centered stats section - hidden on Projects and Profile pages */}
            {!shouldHideStatsAndChat && (
              <div className="w-100 d-flex justify-content-center align-items-center">
                {showStats ? (
                  <div
                    className="d-flex justify-content-center align-items-center gap-3"
                    style={{ paddingTop: '2px' }}
                    onClick={() => setShowStats(false)}
                  >
                    <StatItem>
                      <Image size={18} className="icon" />
                      <div className="stat-content">
                        <p className="stat-value">{projectStats.totalImages}</p>
                        <p className="stat-label">Images</p>
                      </div>
                    </StatItem>
                    <StatItem>
                      <Tag size={18} className="icon" />
                      <div className="stat-content">
                        <p className="stat-value">{projectStats.totalAnnotations}</p>
                        <p className="stat-label">Annotations</p>
                      </div>
                    </StatItem>
                    <StatItem>
                      <Box size={18} className="icon" />
                      <div className="stat-content">
                        <p className="stat-value">{projectStats.totalClasses}</p>
                        <p className="stat-label">Classes</p>
                      </div>
                    </StatItem>
                    <StatItem>
                      <Activity size={18} className="icon" />
                      <div className="stat-content">
                        <p className="stat-value">{projectStats.totalModels}</p>
                        <p className="stat-label">Models</p>
                      </div>
                    </StatItem>
                    <StatItem>
                      <Rocket size={18} className="icon" />
                      <div className="stat-content">
                        <p className="stat-value">{projectStats.deployedModels?.length || '0'}</p>
                        <p className="stat-label">
                          Deployment
                          {projectStats.deployedModels?.length === 1 ? '' : 's'}
                        </p>
                      </div>
                    </StatItem>
                  </div>
                ) : (
                  <StatsIcon
                    onClick={() => {
                      setShowStats(true)
                      setManuallyOpened(true)
                    }}
                  >
                    <BarChart2 size={18} />
                  </StatsIcon>
                )}
              </div>
            )}

            {/* AI Chat Icon - hidden on Projects and Profile pages */}
            {!shouldHideStatsAndChat && (
              <AIChatIcon onClick={() => setShowAIChatModal(true)}>
                <MessageCircle size={18} />
                <BetaTag>BETA</BetaTag>
              </AIChatIcon>
            )}
          </div>
        </StatsRow>

        <AIChatModal show={showAIChatModal} onHide={() => setShowAIChatModal(false)} />
      </HeaderContainer>
    </HeaderRowContainer>
  )
}
